<template>
	<div class="w-full max-w-md space-y-2">
		<div class="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm">
			<div class="-mx-5 -mt-5 rounded-t-2xl bg-green-500 px-4 py-3 text-center text-white">
				<div class="text-[10px] font-bold uppercase tracking-[0.16em] text-green-50">Desempenho</div>
				<div v-if="competence_label" class="mt-1 text-sm font-semibold text-white">
					{{ competence_label }}
				</div>
			</div>
			<div class="mt-3 grid gap-4 items-start" :class="show_bonus ? 'grid-cols-2' : 'grid-cols-1'">
				<div>
					<div class="text-sm font-semibold text-slate-500">Pontuação</div>
					<div class="mt-1 text-sm font-semibold leading-none text-slate-900">{{ totalScore }}</div>
				</div>
				<div v-if="show_bonus">
					<div class="flex items-center gap-2">
						<div class="text-sm font-semibold text-slate-500">Bonificação</div>
						<div class="relative flex items-center">
							<button type="button" class="cursor-pointer text-slate-500" @click="showBonusTooltip = true">
								<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4">
									<path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
								</svg>
							</button>
						</div>
					</div>
					<div class="mt-1 text-sm font-semibold leading-none text-slate-900">{{ bonus_amount }}</div>
				</div>
			</div>
		</div>

		<div v-if="showBonusTooltip" class="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" @click="showBonusTooltip = false">
			<div class="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl" @click.stop>
				<div class="flex items-center justify-between">
					<div class="text-sm font-bold uppercase tracking-[0.16em] text-slate-700">Faixas da competência</div>
					<button type="button" class="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 text-xl font-semibold text-slate-600" @click="showBonusTooltip = false">×</button>
				</div>
				<div class="mt-4 space-y-2">
					<div
						v-for="rule in bonus_rules"
						:key="rule.id || `${rule.score_start}-${rule.score_end}`"
						class="rounded-lg border px-3 py-2 text-sm transition-colors"
						:class="isMatchedBonusRule(rule)
							? 'border-green-500 bg-green-500 text-white'
							: 'border-slate-200 bg-slate-50 text-slate-700'"
					>
						{{ formatRuleLabel(rule) }}
					</div>
				</div>
			</div>
		</div>

		<div
			v-if="hasExcellentRange"
			class="flex items-center gap-2 rounded-xl bg-green-400 p-3 font-medium text-white transition-all w-full"
			:class="scoreType === 'excellent' ? 'opacity-100 shadow-sm' : 'opacity-30'"
		>
			<span class="text-sm">
				🏆 Perfeição - Você e toda a empresa bateram as metas !!
			</span>
		</div>

		<div
			class="flex items-center gap-2 rounded-xl bg-green-200 p-3 font-medium text-green-900 transition-all w-full"
			:class="scoreType === 'positive' ? 'opacity-100 shadow-sm' : 'opacity-30'"
		>
			<span class="text-sm">
				😄 Parabéns! Você está alinhado às expectativas da empresa.
			</span>
		</div>

		<div
			class="flex items-center gap-2 rounded-xl bg-yellow-100 p-3 font-medium text-yellow-900 transition-all w-full"
			:class="scoreType === 'neutral' ? 'opacity-100 shadow-sm' : 'opacity-30'"
		>
			<span class="text-sm">
				🙂 Você está no caminho certo. Com um pouquinho mais de foco você chega lá!
			</span>
		</div>

		<div
			class="flex items-center gap-2 rounded-xl bg-red-600 p-3 font-medium text-white transition-all w-full"
			:class="scoreType === 'negative' ? 'opacity-100 shadow-sm' : 'opacity-30'"
		>
			<span class="text-sm">
				😟 Atenção! Revise as expectativas da empresa e coloque energia em melhorar os seus resultados. :(
			</span>
		</div>
	</div>
</template>

<script>
export default {
	name: 'PdiScore',
	data() {
		return {
			showBonusTooltip: false
		};
	},
	props: {
		structure: {
			type: Array,
			required: true
		},
		score_result: {
			type: Object,
			required: true
		},
		competence_label: {
			type: String,
			default: ''
		},
		show_bonus: {
			type: Boolean,
			default: false
		},
		bonus_amount: {
			type: String,
			default: 'R$ 0,00'
		},
		bonus_rules: {
			type: Array,
			default: () => []
		}
	},
	methods: {
		formatRuleLabel(rule) {
			const amount = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL'
			}).format(Number(rule.bonus_amount || 0));

			return `${rule.score_start} a ${rule.score_end} pontos: ${amount}`;
		},

		isMatchedBonusRule(rule) {
			const scoreStart = Number(rule.score_start);
			const scoreEnd = Number(rule.score_end);

			if (Number.isNaN(scoreStart) || Number.isNaN(scoreEnd)) {
				return false;
			}

			return this.totalScore >= scoreStart && this.totalScore <= scoreEnd;
		}
	},
	computed: {
		totalScore() {
			return this.structure.reduce((total, field) => {
				const value = Number(field.answer);
				return total + (isNaN(value) ? 0 : value);
			}, 0);
		},

		scoreType() {
			const score = Number(this.totalScore);

			const check = (condition, value) => {
				const val = Number(value);
				switch (condition) {
					case '>': return score > val;
					case '>=': return score >= val;
					case '<': return score < val;
					case '<=': return score <= val;
					default: return false;
				}
			};

			const matches = (range) =>
				Object.entries(range).every(([cond, val]) => check(cond, val));

			if (
				this.score_result.excellent &&
				Object.keys(this.score_result.excellent).length &&
				matches(this.score_result.excellent)
			) return 'excellent';

			if (
				this.score_result.positive &&
				Object.keys(this.score_result.positive).length &&
				matches(this.score_result.positive)
			) return 'positive';

			if (
				this.score_result.neutral &&
				Object.keys(this.score_result.neutral).length &&
				matches(this.score_result.neutral)
			) return 'neutral';

			if (
				this.score_result.negative &&
				Object.keys(this.score_result.negative).length &&
				matches(this.score_result.negative)
			) return 'negative';

			return null;
		},

		hasExcellentRange() {
			const range = this.score_result.excellent;
			return (
				range &&
				Object.keys(range).length > 0 &&
				Object.values(range).every(v => !isNaN(Number(v)) && v !== '')
			);
		}
	}
};
</script>
